// src/components/charts.jsx
// Lightweight dependency-free charts (SVG). Colors are passed in as hex so they
// stay consistent with the brand palette. Bars/arcs use a subtle vertical
// gradient and animate in on mount (respecting prefers-reduced-motion via CSS).

import React, { useId } from "react";

// lighten a hex colour toward white by amount t (0..1) for gradient tops
function lighten(hex, t = 0.35) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const mix = (c) => Math.round(parseInt(c, 16) + (255 - parseInt(c, 16)) * t);
  return `rgb(${mix(m[1])}, ${mix(m[2])}, ${mix(m[3])})`;
}

// Donut with a centered total and a legend on the side. The arc animates by
// drawing each segment in (stroke-dashoffset), and the centre value fades up.
export function DonutChart({ data, size = 168, thickness = 20, valueFormat, center }) {
  const uid = useId().replace(/:/g, "");
  const items = (data || []).filter((d) => d.value > 0);
  const total = (data || []).reduce((s, d) => s + d.value, 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const centerVal = center?.value ?? total;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0 max-w-full h-auto">
        <defs>
          {items.map((d, i) => (
            <linearGradient key={i} id={`don-${uid}-${i}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={lighten(d.color, 0.35)} />
              <stop offset="100%" stopColor={d.color} />
            </linearGradient>
          ))}
        </defs>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={thickness}
            stroke="currentColor" className="text-tb-bg-2 dark:text-tb-d-inset"
          />
          {items.map((d, i) => {
            const len = (d.value / total) * c;
            const seg = (
              <circle
                key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={`url(#don-${uid}-${i})`} strokeWidth={thickness} strokeLinecap="round"
                strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset}
                style={{
                  animation: `tb-draw .9s cubic-bezier(.22,1,.36,1) ${0.1 + i * 0.12}s backwards`,
                  // draw from 0 -> len via a custom property the keyframe lands on
                  ["--dash-final"]: -offset,
                  strokeDashoffset: -offset,
                }}
              >
                {/* draw-in: start fully hidden, animate to its slice */}
                <animate
                  attributeName="stroke-dashoffset"
                  from={c}
                  to={-offset}
                  dur="0.9s"
                  begin={`${0.1 + i * 0.12}s`}
                  fill="freeze"
                  calcMode="spline"
                  keySplines="0.22 1 0.36 1"
                  keyTimes="0;1"
                />
              </circle>
            );
            offset += len;
            return seg;
          })}
        </g>
        <text
          x="50%" y={center?.label ? "44%" : "50%"} textAnchor="middle" dominantBaseline="central"
          fill="currentColor" className="text-tb-ink dark:text-tb-d-text font-bold tb-fade-up" fontSize={size * 0.2}
          style={{ animationDelay: "0.5s" }}
        >
          {centerVal}
        </text>
        {center?.label && (
          <text x="50%" y="60%" textAnchor="middle" dominantBaseline="central"
                fill="currentColor" className="text-tb-muted tb-fade-up" fontSize={size * 0.085}
                style={{ animationDelay: "0.6s" }}>
            {center.label}
          </text>
        )}
      </svg>

      <div className="w-full sm:flex-1 min-w-0 space-y-2">
        {(data || []).map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-sm tb-fade-up" style={{ animationDelay: `${0.4 + i * 0.06}s` }}>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.color }} />
            <span className="text-tb-muted truncate">{d.label}</span>
            <span className="ml-auto font-medium tabular-nums shrink-0 pl-2">{valueFormat ? valueFormat(d.value) : d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Semicircle gauge - "fuel left" read for pools, quotas, progress. The value
// arc draws in from empty and uses a gradient along its length.
// Optional `credits` adds a second (never-expiring top-up) segment in a distinct
// colour, picking up where the plan-pool arc ends; total = max + credits.
export function GaugeChart({ value, max, centerValue, centerLabel, color = "#FF6B4A", min = 0, credits = 0, creditColor = "#F5C542" }) {
  const uid = useId().replace(/:/g, "");
  const hasMax = max != null && max > 0;
  const total = hasMax ? max + credits : null;
  const poolPct = hasMax ? Math.min(1, Math.max(0, value / total)) : 1;
  const creditPct = hasMax && credits > 0 ? Math.min(1, Math.max(0, credits / total)) : 0;
  const r = 78, cx = 100, cy = 100, w = 16;

  // pool arc: from the left end, spanning poolPct of the semicircle
  const poolAng = Math.PI * (1 - poolPct);
  const poolXe = cx + r * Math.cos(poolAng);
  const poolYe = cy - r * Math.sin(poolAng);
  // credit arc: continues from where the pool arc ends
  const creditAng = Math.PI * (1 - Math.min(1, poolPct + creditPct));
  const creditXe = cx + r * Math.cos(creditAng);
  const creditYe = cy - r * Math.sin(creditAng);

  const track = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const poolArc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${poolXe} ${poolYe}`;
  const creditArc = `M ${poolXe} ${poolYe} A ${r} ${r} 0 0 1 ${creditXe} ${creditYe}`;
  const poolLen = Math.PI * r * poolPct;
  const creditLen = Math.PI * r * creditPct;

  return (
    <svg viewBox="0 0 200 122" className="w-full max-w-[280px] mx-auto block">
      <defs>
        <linearGradient id={`gauge-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={lighten(color, 0.4)} />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
        <linearGradient id={`gaugec-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={lighten(creditColor, 0.3)} />
          <stop offset="100%" stopColor={creditColor} />
        </linearGradient>
      </defs>
      <path d={track} fill="none" strokeWidth={w} stroke="currentColor"
            className="text-tb-bg-2 dark:text-tb-d-inset" strokeLinecap="round" />
      {poolPct > 0.01 && (
        <path d={poolArc} fill="none" strokeWidth={w} stroke={`url(#gauge-${uid})`} strokeLinecap="round"
              strokeDasharray={poolLen} strokeDashoffset={poolLen}>
          <animate attributeName="stroke-dashoffset" from={poolLen} to="0" dur="1s"
                   begin="0.15s" fill="freeze" calcMode="spline" keySplines="0.22 1 0.36 1" keyTimes="0;1" />
        </path>
      )}
      {creditPct > 0.01 && (
        <path d={creditArc} fill="none" strokeWidth={w} stroke={`url(#gaugec-${uid})`} strokeLinecap="round"
              strokeDasharray={creditLen} strokeDashoffset={creditLen}>
          <animate attributeName="stroke-dashoffset" from={creditLen} to="0" dur="0.6s"
                   begin="1.05s" fill="freeze" calcMode="spline" keySplines="0.22 1 0.36 1" keyTimes="0;1" />
        </path>
      )}
      <text x="100" y="84" textAnchor="middle" fill={color} className="font-bold tb-fade-up" fontSize="38"
            style={{ animationDelay: "0.5s" }}>{centerValue}</text>
      <text x="100" y="103" textAnchor="middle" fill="currentColor" className="text-tb-muted tb-fade-up" fontSize="11"
            style={{ animationDelay: "0.6s" }}>{centerLabel}</text>
      <text x="22" y="118" textAnchor="middle" fill="currentColor" className="text-tb-muted" fontSize="10">{min}</text>
      <text x="178" y="118" textAnchor="middle" fill="currentColor" className="text-tb-muted" fontSize="10">{hasMax ? total : "\u221e"}</text>
    </svg>
  );
}

// Vertical bar chart with value labels and category labels. Bars rise in,
// staggered, with a vertical gradient.
export function BarChart({ data, height = 200 }) {
  const max = Math.max(1, ...(data || []).map((d) => d.value));
  const plot = height - 28;

  return (
    <div>
      <div className="flex items-end gap-4" style={{ height: plot }}>
        {(data || []).map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end">
            <span className="text-sm font-semibold tabular-nums mb-1.5 tb-fade-up" style={{ animationDelay: `${0.15 + i * 0.08}s` }}>{d.value}</span>
            <div
              className="w-full max-w-[72px] rounded-t-lg tb-rise"
              style={{
                height: Math.max(d.value > 0 ? 6 : 2, (d.value / max) * (plot - 26)),
                background: `linear-gradient(to top, ${d.color}, ${lighten(d.color, 0.3)})`,
                animationDelay: `${i * 0.08}s`,
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-2">
        {(data || []).map((d, i) => (
          <span key={i} className="flex-1 text-[11px] text-tb-muted text-center truncate">{d.label}</span>
        ))}
      </div>
    </div>
  );
}

// Horizontal bars - good for labelled category lists. Bars grow in from the
// left, staggered, with a horizontal gradient.
export function HBarChart({ data, valueFormat }) {
  const items = (data || []).filter((d) => d.value > 0);
  const max = Math.max(1, ...items.map((d) => d.value));
  return (
    <div className="space-y-3">
      {items.map((d, i) => {
        const color = d.color || "#FF6B4A";
        return (
          <div key={i} className="tb-fade-up" style={{ animationDelay: `${i * 0.07}s` }}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-tb-muted truncate">{d.label}</span>
              <span className="font-medium tabular-nums ml-3">{valueFormat ? valueFormat(d.value) : d.value}</span>
            </div>
            <div className="h-2 rounded-full bg-tb-bg-2 dark:bg-tb-d-inset overflow-hidden">
              <div
                className="h-full rounded-full tb-grow"
                style={{
                  width: `${Math.max(2, (d.value / max) * 100)}%`,
                  background: `linear-gradient(to right, ${color}, ${lighten(color, 0.3)})`,
                  animationDelay: `${0.1 + i * 0.07}s`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}